<template>
  <button
    @click="() => (!disabled ? $emit('click', $event) : false)"
    :class="[baseClasses, colorClasses, sizeClasses, disabledClasses, widthClasses, fullClasses]"
    v-if="disabled || (href === null && to === null)"
    :type="typeEvent"
    :disabled="disabled"
    :id="id"
  >
    <BaseIcon
      style="font-size: inherit"
      v-if="loading || icon"
      :name="loading ? 'sync' : icon"
      :class="[
        !['icon', 'icon-sm'].includes(size) ? 'mr-2' : '',
        'text-inherit!',
        {
          'animate-spin': loading,
        },
      ]"
    />
    <slot v-if="!loading" />
    <template v-else>{{ $t(loadingText || 'Please wait...') }}</template>
  </button>
  <a
    @click="disabled ? null : $emit('click', $event)"
    :class="[baseClasses, colorClasses, sizeClasses, disabledClasses, widthClasses, fullClasses]"
    :target="target"
    :href="href"
    v-else-if="href !== null"
    :id="id"
  >
    <BaseIcon
      style="font-size: inherit"
      v-if="icon"
      :name="icon"
      :class="[!['icon', 'icon-sm'].includes(size) ? 'mr-2' : '', 'text-inherit!']"
    />
    <slot />
  </a>
  <router-link
    @click="disabled ? null : $emit('click', $event)"
    :class="[baseClasses, colorClasses, sizeClasses, disabledClasses, widthClasses, fullClasses]"
    :target="target"
    :to="to"
    :id="id"
    :preserve-scroll="preserveScroll"
    :preserve-state="preserveState"
    v-else-if="to !== null"
  >
    <BaseIcon
      style="font-size: inherit"
      v-if="icon"
      :name="icon"
      :class="[!['icon', 'icon-sm'].includes(size) ? 'mr-2' : '', 'text-inherit!']"
    />
    <slot />
  </router-link>
</template>

<script>
export default {
  props: {
    href: {
      type: String,
      default: null,
    },
    to: {
      type: [String, Object],
      default: null,
    },
    id: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      default: '',
    },
    theme: {
      type: String,
      default: 'primary',
    },
    typeEvent: {
      type: String,
      default: null,
    },
    size: {
      type: String,
      default: 'md',
    },
    color: {
      type: String,
      default: 'primary',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadingText: {
      type: String,
      default: 'Please wait...',
    },
    noLabel: {
      type: Boolean,
      default: false,
    },
    target: {
      type: String,
      default: null,
    },
    preserveScroll: {
      type: Boolean,
      default: false,
    },
    preserveState: {
      type: Boolean,
      default: false,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    thin: {
      type: Boolean,
      default: false,
    },
    full: {
      type: Boolean,
      default: false,
    },
    flat: {
      type: Boolean,
      default: false,
    },
    min: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: null,
    },
  },
  emits: ['click'],
  computed: {
    baseClasses() {
      return 'relative inline-flex items-center focus:outline-hidden text-center justify-center border font-semibold focus:outline-hidden transition ease-in-out duration-150';
    },

    colorClasses() {
      return {
        primary: [this.focusColorClasses, this.primaryColorClasses],
        lightblue: [this.focusColorClasses, this.primaryColorClasses],
        secondary: [this.focusSecondaryColorClasses, this.secondaryColorClasses],
        white: [this.focusColorClasses, this.primaryColorClasses],
        danger: [this.focusColorClasses, this.primaryColorClasses],
        action: [this.focusColorClasses, this.primaryColorClasses],
      }[this.theme];
    },

    focusColorClasses() {
      if (this.flat) {
        return (
          {
            primary:
              'focus:bg-primary focus:border focus:border-primary focus:bg-primary focus:text-white hover:border hover:border-primary hover:bg-primary hover:text-white dark:hover:text-white active:border active:border-primary active:bg-primary active:text-primary',
            danger:
              'focus:bg-danger focus:text-white hover:bg-danger hover:text-white active:bg-danger active:text-white',
            action:
              'focus:bg-secondary focus:text-white hover:bg-secondary hover:text-white active:bg-secondary active:text-white',
            secondary: 'text-gray-600 bg-gray-1000',
          }[this.color] ?? ''
        );
      }
      return (
        {
          primary: this.outlined
            ? 'focus:bg-primary focus:text-white hover:bg-primary hover:text-white active:bg-blue-400 active:text-white'
            : 'focus:bg-primary focus:border focus:border-primary/20 focus:bg-transparent focus:text-primary hover:border hover:border-primary/20 hover:bg-primary/10 hover:text-primary dark:hover:text-white active:border active:border-primary/20 active:bg-transparent active:text-primary ',
          white: this.outlined
            ? 'focus:bg-white focus:text-blue-200 hover:bg-white hover:text-blue-200 active:bg-white active:text-blue-400'
            : 'focus:bg-primary focus:text-white hover:bg-blue-200 hover:text-white active:bg-blue-400 active:text-white',
          danger: this.outlined
            ? 'focus:bg-danger focus:text-white hover:bg-danger hover:text-white active:bg-danger active:text-white'
            : 'focus:bg-red-500 focus:text-white hover:bg-red-700 hover:text-white active:bg-red-400 active:text-white',
          secondary: this.outlined
            ? 'focus:bg-danger focus:text-white hover:bg-secondary hover:text-white active:bg-secondary active:text-white dark:text-white'
            : 'focus:bg-secondary focus:text-white hover:bg-secondary hover:text-white active:bg-secondary active:text-white',
          action: this.outlined
            ? 'focus:bg-transparent focus:border focus:border-secondary focus:text-secondary hover:bg-transparent hover:border hover:border-secondary hover:text-secondary active:bg-transparent active:border active:border-secondary active:text-secondary'
            : 'focus:bg-transparent focus:text-secondary hover:bg-transparent hover:text-secondary active:bg-transparent active:text-secondary disabled:hover:text-white',
        }[this.color] ?? ''
      );
    },

    focusSecondaryColorClasses() {
      return (
        {
          primary: this.outlined
            ? 'focus:bg-transparent focus:border focus:border-blue-200 focus:text-blue-200 hover:bg-transparent hover:border hover:border-blue-200 hover:text-blue-200 active:bg-transparent active:border active:border-blue-400 active:text-blue-400'
            : 'focus:bg-blue-200 focus:text-white hover:bg-blue-200 hover:text-white active:bg-blue-400 active:text-white',
          white: this.outlined
            ? 'focus:bg-transparent focus:border focus:border-blue-200 focus:text-blue-200 hover:bg-transparent hover:border hover:border-blue-200 hover:text-blue-200 active:bg-transparent active:border active:border-blue-400 active:text-blue-400'
            : 'focus:bg-blue-200 focus:text-white hover:bg-blue-200 hover:text-white active:bg-blue-400 active:text-white',
          black: this.outlined
            ? 'focus:bg-transparent focus:border focus:border-blue-200 focus:text-blue-200 hover:bg-transparent hover:border hover:border-blue-200 hover:text-blue-200 active:bg-transparent active:border active:border-blue-400 active:text-blue-400'
            : 'focus:bg-blue-200 focus:text-white hover:bg-blue-200 hover:text-white active:bg-blue-400 active:text-white',
          danger: this.outlined
            ? 'focus:bg-transparent focus:border focus:border-blue-200 focus:text-blue-200 hover:bg-transparent hover:border hover:border-blue-200 hover:text-blue-200 active:bg-transparent active:border active:border-blue-400 active:text-blue-400'
            : 'focus:bg-blue-200 focus:text-white hover:bg-blue-200 hover:text-white active:bg-blue-400 active:text-white',
          action: this.outlined
            ? 'focus:bg-transparent focus:border focus:border-secondary focus:text-secondary hover:bg-transparent hover:border hover:border-secondary hover:text-secondary active:bg-transparent active:border active:border-secondary active:text-secondary'
            : 'focus:bg-transparent focus:text-secondary hover:bg-transparent hover:text-secondary active:bg-transparent active:text-secondary',
        }[this.color] ?? ''
      );
    },

    primaryColorClasses() {
      if (this.flat) {
        return (
          {
            primary:
              'text-primary bg-primary/10 dark:bg-primary dark:text-white dark:border-primary',
            danger: 'text-danger bg-danger/20 dark:bg-danger dark:text-white dark:border-danger',
            action:
              'text-secondary bg-secondary/20 dark:bg-secondary dark:text-white dark:border-secondary',
            secondary:
              'text-gray-600 bg-gray-100 dark:bg-primary dark:text-white dark:border-primary',
          }[this.color] ?? ''
        );
      }
      return (
        {
          lightblue: this.outlined
            ? 'border border-gray-100 bg-transparent text-gray-100'
            : 'bg-gray-100 text-secondary',
          white: this.outlined
            ? 'border border-white bg-transparent text-white'
            : 'bg-white text-blue-200 ',
          primary: this.outlined
            ? 'border border-primary bg-transparent text-primary dark:text-gray-100 dark:border-gray-100'
            : 'bg-primary text-white border-primary',
          danger: this.outlined
            ? 'border border-red-500 bg-transparent text-red-500'
            : 'bg-red-500 text-white border-red-500',
          action: this.outlined
            ? 'border border-secondary bg-transparent text-secondary'
            : 'bg-secondary disabled:bg-secondary/80 disabled:border-secondary/10 text-white border-secondary',
        }[this.color] ?? ''
      );
    },

    secondaryColorClasses() {
      return (
        {
          white: this.outlined
            ? 'border border-white bg-transparent text-white'
            : 'bg-white text-blue-200 ',
          black: this.outlined
            ? 'border border-gray-500 bg-transparent text-gray-500 dark:text-gray-200'
            : 'bg-white text-gray-200 ',
          primary: this.outlined
            ? 'border border-gray-500 bg-transparent text-gray-500 dark:text-gray-200'
            : 'bg-white text-gray-200 ',
        }[this.color] ?? ''
      );
    },

    sizeClasses() {
      let sizes = null;
      if (this.noLabel) {
        return 'p-2 rounded-full';
      }
      if (!!this.icon && !this.noLabel) {
        sizes = {
          xs: 'leading-4 text-xs pl-1.5 pr-2.5 py-1.5 rounded-md',
          'icon-sm': 'grid place-content-center text-lg! p-2 size-sm rounded-full ',
          icon: 'text-2xl p-2 size-xl rounded-full ',
          sm: 'text-sm pl-4 pr-5 py-2 size-sm rounded-2xl ',
          md: 'text-base pl-5 pr-7 py-2.5 size-md rounded-2xl ',
          lg: 'text-lg pl-4 pr-6 py-3 size-lg rounded-2xl ',
          xl: 'leading-6 text-xl pl-6 pr-8 py-3 rounded-2xl ',
        }[this.size];
      } else {
        sizes = {
          xs: 'leading-4 text-xs! px-2.5 py-1.5 rounded-md',
          sm: 'text-sm px-4 py-2 size-sm rounded-2xl ',
          md: 'text-base px-7 py-2.5 size-md rounded-2xl ',
          lg: 'text-lg px-8 py-3 size-lg rounded-2xl ',
          xl: 'leading-6 text-2xl px-10 py-4 rounded-2xl ',
        }[this.size];
      }

      if (this.min) {
        sizes = sizes + ' min-w-[120px]';
      }
      return sizes;
    },

    disabledClasses() {
      return this.disabled ? 'cursor-not-allowed opacity-75' : 'cursor-pointer';
    },

    widthClasses() {
      return this.thin ? 'font-thin' : 'font-semibold';
    },

    fullClasses() {
      return this.full ? 'w-full' : '';
    },
  },
};
</script>

<style scoped lang="scss">
// button,
// a {
//     border-radius: 1.25rem;
// }

// .size {
//     &-md {
//         height: 50px;
//         padding: 0 40px;
//     }

//     &-sm {
//         height: 40px;
//         padding: 0 30px;
//     }

//     &-lg {
//         height: 60px;
//         padding: 0 40px;
//     }
// }
</style>
